import { defineFakeRoute } from 'vite-plugin-fake-server/client';
import { SecurityDomainError } from '@/services/business/security/problems';
import { accountStore } from '../store/account';
import { securityResult } from './security-errors';
import {
  accessPolicyResource,
  createIpRule,
  deleteIpRule,
  processSecurityEvent,
  releaseSecurityRestriction,
  securityEvents,
  securityIpRules,
  securitySessions,
  terminateSecuritySession,
  updateIpRule,
  updateLoginPolicy,
  updateProtectionPolicy,
} from './security-state';
import { record } from './security-validation';
import { routeParam } from './utils';

function actor() {
  const current = accountStore.currentUser();
  if (!('userid' in current)) {
    throw new SecurityDomainError('permissionDenied', 401);
  }
  return { id: current.userid, name: current.name };
}
function withId(body: unknown, id: string) {
  if (!record(body)) {
    throw new SecurityDomainError('invalidPolicy', 422);
  }
  return { ...body, id };
}
export default defineFakeRoute([
  {
    url: '/platform/security/events',
    method: 'get',
    response: ({ query }) => securityResult(() => securityEvents(query)),
  },
  {
    url: '/platform/security/events/:id/resolve',
    method: 'post',
    response: ({ body, params }) =>
      securityResult(() =>
        processSecurityEvent(routeParam(params.id) ?? '', body, actor()),
      ),
  },
  {
    url: '/platform/security/events/:id/release',
    method: 'post',
    response: ({ body, params }) =>
      securityResult(() =>
        releaseSecurityRestriction(routeParam(params.id) ?? '', body, actor()),
      ),
  },
  {
    url: '/platform/security/sessions',
    method: 'get',
    response: ({ query }) => securityResult(() => securitySessions(query)),
  },
  {
    url: '/platform/security/sessions/:id/revoke',
    method: 'post',
    response: ({ body, params }) =>
      securityResult(() => terminateSecuritySession(routeParam(params.id) ?? '', body)),
  },
  {
    url: '/platform/security/access-policy',
    method: 'get',
    response: () => securityResult(accessPolicyResource),
  },
  {
    url: '/platform/security/access-policy/login',
    method: 'patch',
    response: ({ body }) => securityResult(() => updateLoginPolicy(body)),
  },
  {
    url: '/platform/security/access-policy/protection',
    method: 'patch',
    response: ({ body }) => securityResult(() => updateProtectionPolicy(body)),
  },
  {
    url: '/platform/security/ip-rules',
    method: 'get',
    response: ({ query }) => securityResult(() => securityIpRules(query)),
  },
  {
    url: '/platform/security/ip-rules',
    method: 'post',
    response: ({ body }) => securityResult(() => createIpRule(body)),
  },
  {
    url: '/platform/security/ip-rules/:id',
    method: 'patch',
    response: ({ body, params }) =>
      securityResult(() => {
        const id = routeParam(params.id) ?? '';
        const input = withId(body, id);
        // The command boundary validates all unknown fields before changing state.
        return updateIpRule(input);
      }),
  },
  {
    url: '/platform/security/ip-rules/:id',
    method: 'delete',
    response: ({ body, params }) =>
      securityResult(() => {
        const id = routeParam(params.id) ?? '';
        return deleteIpRule(withId(body, id));
      }),
  },
]);
